"use client";

import { DefinitionList } from "~/components/common/DefinitionDisplay";
import { Card, CardContent, CardFooter } from "~/components/ui/card";
import { cn } from "~/lib/utils";
import { type SelectLemma } from "~/server/db/lemmas/schema";
import { type SelectLexemeWithLemma } from "~/server/db/lexemes/schema";
import { type SelectTranscriptWithLexemes } from "~/server/db/transcripts/schema";
import { type Example } from "~/server/pipeline/steps/common/types";
import { useTrackLemmas } from "~/state/lemmas/hooks/useTrackLemmas";

import { type OnShowFullCard } from "./CardFullProvider";
import WordCardHeader from "./CardHeader";

const SHOW_DEFINITIONS_COUNT = 2;
const MAX_DOTS = 10;

export default function WordCard({
  lexeme,
  lemma,
  transcript,
  onShowFullCard,
  isMobile,
  reverseControls = false,
}: {
  lexeme?: SelectLexemeWithLemma;
  lemma: SelectLemma;
  transcript?: SelectTranscriptWithLexemes;
  isMobile: boolean;
  onShowFullCard: OnShowFullCard;
  reverseControls?: boolean;
}) {
  const { isLemmaTracked } = useTrackLemmas();

  const isKnown = isLemmaTracked(lemma.id);

  const { examples = [] as Example[] } = lemma;
  const definitions = lemma.definitions?.en?.slice(0, SHOW_DEFINITIONS_COUNT);
  const definitionCount = lemma.definitions?.en?.length ?? 0;
  const examplesCount = examples?.length ?? 0;

  const handleCardClick = () => {
    onShowFullCard(lemma, lexeme, transcript);
  };

  return (
    <Card
      className={cn("mb-4 w-full cursor-pointer", {
        "hover:opacity-50": isKnown,
        "opacity-25": isKnown,
      })}
      style={{ transformStyle: "preserve-3d" }}
      onClick={handleCardClick}
    >
      <div className="group flex h-full w-full flex-col hover:shadow-lg">
        <WordCardHeader
          isMobile={isMobile}
          lexeme={lexeme}
          lemma={lemma}
          transcript={transcript}
          reverseControls={reverseControls}
        />
        <CardContent className="flex-grow overflow-hidden pb-0">
          <div className="mb-4">
            <DefinitionList definitions={definitions ?? []} />
          </div>
        </CardContent>

        <CardFooter className="text-md my-0 justify-center py-0 pb-2">
          {renderDots(
            definitionCount - SHOW_DEFINITIONS_COUNT,
            "opacity-20",
            MAX_DOTS,
          )}
          {renderDots(examplesCount, "opacity-100", MAX_DOTS)}
        </CardFooter>
      </div>
    </Card>
  );
}

const renderDots = (count: number, className?: string, maxDots?: number) =>
  Array.from({ length: Math.min(count, maxDots ?? count) }).map((_, i) => (
    <span key={i} className={cn("mx-0.5", className)}>
      •
    </span>
  ));
